import axios from 'axios'
import { APP_NAME, ROUTES } from '../static/config'
import { createBrowserHistory } from 'history'
import { httpcodeIsOK } from './helpers'

const baseUrl = process.env.REACT_APP_BASE_URL
const history = createBrowserHistory()

const api = {
  login: (body) => {
    return axios.post(`${baseUrl}/auth/token/access`, body)
  },
  forgotPassword: (body) => {
    return axios.post(`${baseUrl}/auth/password/forgot`, body)
  },
  confirmPassword: (body) => {
    return axios.put(`${baseUrl}/auth/password/forgot`, body)
  },
  changePassword: (body) => {
    return axios.post(`${baseUrl}/auth/password/reset`, body)
  },
  refreshToken: (refreshToken) => {
    return axios.post(
      `${baseUrl}/auth/token/refresh`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer  ${refreshToken}`
        }
      }
    )
  },
  getDashboardStats: (params) => {
    return axios.get(`${baseUrl}/dashboard`, {
      params: {
        time_from: params && params.time_from ? params.time_from : null,
        time_to: params && params.time_to ? params.time_to : null
      }
    })
  },
  getTripsInProgress: (data) => {
    return axios.get(
      `${baseUrl}/trips/progress`,
      {
        params: {
          page: data.page
        }
      }
    )
  },
  getVehicles: (params) => {
    return axios.get(`${baseUrl}/vehicles`, {
      params: {
        page: params && params.page ? params.page : null,
        search: params && params.search ? params.search : null
      }
    })
  },
  getVehicleDetail: (vehicleId) => {
    return axios.get(`${baseUrl}/vehicles/${vehicleId}`)
  },
  getLastPositions: () => {
    return axios.get(`${baseUrl}/vehicles/positions/last`)
  },
  getVehicleLastPositions: (vehicleId) => {
    return axios.get(`${baseUrl}/vehicles/positions/last/${vehicleId}`)
  },
  getVehicleTrips: (vehicleId, params) => {
    return axios.get(`${baseUrl}/vehicles/${vehicleId}/trips`, {
      params: {
        page: params && params.page ? params.page : null,
        time_from: params && params.time_from ? params.time_from : null,
        time_to: params && params.time_to ? params.time_to : null
      }
    })
  },
  createNewVehicle: (body) => {
    return axios.post(`${baseUrl}/vehicles`, body)
  },
  editVehicle: (vehicleId, body) => {
    return axios.put(`${baseUrl}/vehicles/${vehicleId}`, body)
  },
  deleteVehicle: (vehicleId) => {
    return axios.delete(`${baseUrl}/vehicles/${vehicleId}`)
  },
  createNewVehicleMedia: (file, vehicleId) => {
    return axios.post(`${baseUrl}/vehicles/${vehicleId}/media`, file, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  deleteVehicleMedia: (vehicleId, mediaId) => {
    return axios.delete(`${baseUrl}/vehicles/${vehicleId}/media/${mediaId}`)
  },
  getTrips: (params) => {
    return axios.get(`${baseUrl}/trips`, {
      params: {
        page: params && params.page ? params.page : null,
        time_from: params && params.time_from ? params.time_from : null,
        time_to: params && params.time_to ? params.time_to : null
      }
    })
  },
  getTripDetail: (tripId, type) => {
    return axios.get(`${baseUrl}/trips/${tripId}`, {
      params: {
        type: type
      }
    })
  },
  getTripEvent: (tripId) => {
    return axios.get(
      `${baseUrl}/trips/${tripId}/events`
    )
  },
  getTripCrash: (tripId, data) => {
    return axios.get(
      `${baseUrl}/trips/${tripId}/crashes`
    )
  },
  getDevices: (params) => {
    return axios.get(`${baseUrl}/devices`, {
      params: {
        page: params && params.page ? params.page : null,
        search: params && params.search ? params.search : null
      }
    })
  },
  getAvailableDevices: () => {
    return axios.get(
      `${baseUrl}/devices`,
      {
        params: {
          available: 'true'
        }
      }
    )
  },
  getDeviceDetail: (deviceId) => {
    return axios.get(`${baseUrl}/devices/${deviceId}`)
  },
  createNewDevice: (body) => {
    return axios.post(`${baseUrl}/devices`, body)
  },
  editDevice: (deviceId, body) => {
    return axios.put(`${baseUrl}/devices/${deviceId}`, body)
  },
  deleteDevice: (deviceId) => {
    return axios.delete(`${baseUrl}/devices/${deviceId}`)
  },
  getVehicleModels: () => {
    return axios.get(`${baseUrl}/catalog/vehicle/models`)
  },
  getEventsType: () => {
    return axios.get(`${baseUrl}/catalog/events`)
  },
  getEcoScoreDecode: () => {
    return axios.get(`${baseUrl}/catalog/eco_score_decode`)
  },
  geoNearbyStreet: (lat, lon) => {
    return axios.get(
      `${baseUrl}/geo/find_nearby_streets`,
      {
        params: {
          lat: lat, lon: lon
        }
      }
    )
  },
  searchCoordsFromDateTime: (vehicleId, datetime) => {
    return axios.get(`${baseUrl}/search/coordinates_from_datetime`, {
      params: { vehicle_id: vehicleId, datetime }
    })
  }
}

axios.interceptors.request.use(
  (config) => {
    const $storage = JSON.parse(localStorage.getItem(APP_NAME))
    const accessToken = $storage ? $storage.access_token : null
    if (accessToken) {
      if (!config.url.includes('/auth/token/refresh')) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }
    }
    config.headers['Access-Control-Expose-Headers'] = '*'
    return config
  },
  (error) => {
    Promise.reject(error)
  }
)

axios.interceptors.response.use(
  (response) => {
    return response
  },
  async function (error) {
    const UNAUTH = 401
    const originalRequest = error.config
    const $storage = JSON.parse(localStorage.getItem(APP_NAME))
    const refreshToken = $storage ? $storage.refresh_token : null

    if (!error.response) return Promise.reject(error)

    if (error.response.status === UNAUTH && originalRequest.url.includes('/auth/token/refresh')) {
      localStorage.removeItem(APP_NAME)
      window.location.reload()
      return Promise.reject(error)
    }

    if (error.response.status === UNAUTH && !originalRequest._retry) {
      originalRequest._retry = true
      if (refreshToken) {
        const res = await api.refreshToken(refreshToken)
        if (httpcodeIsOK(res.status)) {
          const storage = JSON.parse(localStorage.getItem(APP_NAME))
          storage.access_token = res.data.access_token
          localStorage.setItem(APP_NAME, JSON.stringify(storage))
          axios.defaults.headers.common.Authorization = `Bearer ${storage.access_token}`
          return axios(originalRequest)
        }
      }
      history.push(ROUTES.auth)
    }
    return Promise.reject(error)
  }
)

export default api
